import React, { useState } from 'react';
import { formatCurrencyAmount } from '../../utils/profitability';
import './ProfitabilityLineChart.css';

const WIDTH = 760;
const HEIGHT = 320;
const PADDING = {
  top: 20, right: 20, bottom: 32, left: 64
};

const LINE_COLORS = ['#10708f', '#043e53', '#2f9e63', '#b2352c', '#8a5cf6'];
const POINT_COLOR = 'var(--accent-500)';
const ZERO_COLOR = 'var(--ink-300)';
const AXIS_STEP = 500;
const EDGE_MARGIN = 0.12;

const formatAxisValue = (value) => {
  const abs = Math.abs(value);
  if (abs >= 1000) return `${(value / 1000).toFixed(abs >= 10000 ? 0 : 1)}k`;
  return String(Math.round(value));
};

const ProfitabilityLineChart = ({
  months, currencies, data, year
}) => {
  const [hovered, setHovered] = useState(null);

  const plotWidth = WIDTH - PADDING.left - PADDING.right;
  const plotHeight = HEIGHT - PADDING.top - PADDING.bottom;

  const allValues = data.flatMap((row) => currencies.map((c) => row.values[c] ?? 0));
  const rawMax = allValues.length ? Math.max(...allValues) : 0;
  const rawMin = allValues.length ? Math.min(...allValues) : 0;

  let spanMax = Math.ceil(Math.max(rawMax, 0) / AXIS_STEP) * AXIS_STEP;
  const spanMin = Math.floor(Math.min(rawMin, 0) / AXIS_STEP) * AXIS_STEP;
  if (spanMax === spanMin) spanMax = spanMin + AXIS_STEP;
  const span = spanMax - spanMin;

  const xFor = (index) => (months.length <= 1
    ? PADDING.left + plotWidth / 2
    : PADDING.left + (index / (months.length - 1)) * plotWidth);
  const yFor = (value) => PADDING.top + plotHeight - ((value - spanMin) / span) * plotHeight;

  const zeroY = yFor(0);

  const gridSteps = Math.round(span / AXIS_STEP);
  const gridLines = Array.from({ length: gridSteps + 1 }, (_, i) => {
    const value = spanMin + i * AXIS_STEP;
    return { value, y: yFor(value) };
  });

  const isZeroMonth = (index) => currencies.every((c) => !(data[index]?.values[c]));

  let tooltipAnchor = 'center';
  if (hovered) {
    const ratio = hovered.x / WIDTH;
    if (ratio < EDGE_MARGIN) tooltipAnchor = 'start';
    else if (ratio > 1 - EDGE_MARGIN) tooltipAnchor = 'end';
  }

  const tooltipStyle = hovered ? {
    left: `${(hovered.x / WIDTH) * 100}%`,
    top: `${(hovered.y / HEIGHT) * 100}%`
  } : null;

  const yearTotals = currencies.map((currency) => ({
    currency,
    total: data.reduce((sum, row) => sum + (row.values[currency] ?? 0), 0)
  }));

  return (
    <div className="profitability-chart">
      <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} role="img" aria-label="Ganancia mensual por moneda">
        {gridLines.map((line) => (
          <g key={line.value}>
            <line
              x1={PADDING.left}
              x2={WIDTH - PADDING.right}
              y1={line.y}
              y2={line.y}
              className="chart-grid-line"
            />
            <text x={PADDING.left - 8} y={line.y} className="chart-axis-label" textAnchor="end" dominantBaseline="middle">
              {formatAxisValue(line.value)}
            </text>
          </g>
        ))}

        <line
          x1={PADDING.left}
          x2={WIDTH - PADDING.right}
          y1={zeroY}
          y2={zeroY}
          className="chart-zero-line"
        />

        {months.map((m, index) => (
          <text
            key={m.value}
            x={xFor(index)}
            y={HEIGHT - PADDING.bottom + 18}
            className={`chart-axis-label${isZeroMonth(index) ? ' chart-axis-label--muted' : ''}`}
            textAnchor="middle"
          >
            {m.short}
          </text>
        ))}

        {currencies.map((currency, ci) => {
          const color = LINE_COLORS[ci % LINE_COLORS.length];
          const points = data.map((row, index) => ({
            x: xFor(index),
            y: yFor(row.values[currency] ?? 0),
            value: row.values[currency] ?? 0,
            label: months[index]?.label,
            key: `${currency}-${months[index]?.value}`
          }));

          return (
            <g key={currency}>
              {points.slice(1).map((p, i) => {
                const prev = points[i];
                const isZeroSegment = prev.value === 0 || p.value === 0;
                return (
                  <line
                    key={p.key}
                    x1={prev.x}
                    y1={prev.y}
                    x2={p.x}
                    y2={p.y}
                    stroke={isZeroSegment ? ZERO_COLOR : color}
                    strokeWidth="2.5"
                  />
                );
              })}
              {points.map((p) => (
                <circle
                  key={p.key}
                  cx={p.x}
                  cy={p.y}
                  r={hovered?.key === p.key ? 8 : 5}
                  fill={p.value === 0 ? ZERO_COLOR : POINT_COLOR}
                  className="chart-point"
                  tabIndex={0}
                  role="button"
                  aria-label={`${p.label}: ${currency} ${formatCurrencyAmount(p.value, currency)}`}
                  onMouseEnter={() => setHovered({ ...p, currency })}
                  onMouseLeave={() => setHovered(null)}
                  onFocus={() => setHovered({ ...p, currency })}
                  onBlur={() => setHovered(null)}
                />
              ))}
            </g>
          );
        })}
      </svg>

      {hovered && (
        <div className={`chart-tooltip chart-tooltip--${tooltipAnchor}`} style={tooltipStyle}>
          <div className="chart-tooltip-month">{hovered.label}</div>
          <div className="chart-tooltip-value">
            {hovered.currency}
            {' '}
            {formatCurrencyAmount(hovered.value, hovered.currency)}
          </div>
        </div>
      )}

      <div className="chart-year-total">
        <span className="chart-year-total-label">{`Total ${year}`}</span>
        <div className="chart-year-total-values">
          {yearTotals.map(({ currency, total }) => (
            <span
              key={currency}
              className={`chart-year-total-value ${total > 0 ? 'positive' : total < 0 ? 'negative' : ''}`}
            >
              {currency}
              {' '}
              {formatCurrencyAmount(total, currency)}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProfitabilityLineChart;
